import { useState } from "react";

import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSaveInfraSettings } from "@/features/servers/hooks/useInfraSettings";
import { DEFAULT_REVISION_INTERVAL_MONTHS } from "@/features/servers/schemas/servers";

interface RevisionIntervalEditorProps {
  intervalMonths: number;
}

/**
 * Intervalo global de revisión de hosts/software.
 */
export function RevisionIntervalEditor({
  intervalMonths,
}: RevisionIntervalEditorProps) {
  const saveSettings = useSaveInfraSettings();
  const [draft, setDraft] = useState("");
  const value =
    draft !== ""
      ? draft
      : String(intervalMonths || DEFAULT_REVISION_INTERVAL_MONTHS);

  const handleSave = async () => {
    const n = Number.parseInt(value, 10);
    if (!Number.isFinite(n) || n < 1 || n > 60) {
      toast.error("Indicá un intervalo entre 1 y 60 meses");
      return;
    }
    try {
      await saveSettings.mutateAsync({ revisionIntervalMonths: n });
      setDraft("");
      toast.success(`Revisión cada ${n} mes(es)`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "No se pudo guardar el intervalo"
      );
    }
  };

  return (
    <Card>
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium">
          Intervalo de revisión
        </CardTitle>
        <CardDescription>
          Cada cuántos meses validar hosts y software en las fichas de
          infraestructura.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-3 pt-0">
        <div className="space-y-1.5">
          <Label
            htmlFor="revision-months"
            className="text-xs text-muted-foreground"
          >
            Meses
          </Label>
          <Input
            id="revision-months"
            type="number"
            min={1}
            max={60}
            className="w-28"
            value={value}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleSave()}
          disabled={saveSettings.isPending}
        >
          {saveSettings.isPending ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1.5" />
          )}
          Guardar
        </Button>
      </CardContent>
    </Card>
  );
}
